import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    CircularProgress,
    Typography,
    Box,
    Fab,
    Drawer,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import API_BASE_URL from '../../../../Constants';

const GroupNumberDevices = () => {
    const { groupNumber } = useParams();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (!groupNumber) {
                console.error("No groupNumber available");
                return;
            }
            try {
                const url = `${API_BASE_URL}/api/admin/getDobDevicesPage?page=0&size=10000`;
        
                const response = await fetch(url, {
                    method: "GET",
                    credentials: "include",
                });
        
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
        
                const data = await response.json();
                const filteredDevices = data.content.filter(device => device.groupNumber === groupNumber);
                setDevices(filteredDevices);
            } catch (error) {
                console.error("Fetch error:", error.message || error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [groupNumber]);

    const handleDeviceClick = (deviceID) => {
        navigate(`/inspections-violations/device-defects/${deviceID}`);
    };

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setSidebarOpen(open);
    };

    if (loading) {
        return (
            <Box className="flex justify-center items-center min-h-screen">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="p-6 relative" sx={{ backgroundColor: '#f5f5f5' }}>
            {/* Sidebar Drawer */}
            <Drawer
                anchor="left"
                open={sidebarOpen}
                onClose={toggleDrawer(false)}
                variant="persistent"
                sx={{
                    '& .MuiDrawer-paper': {
                        width: sidebarOpen ? 250 : 0,
                        transition: 'width 0.3s',
                        top: '72px',
                        borderRadius: '0px 8px 8px 0px',
                        background: '#ffffff',
                        borderTop: '2px solid #3182ce', // Using Tailwind sss-blue equivalent
                        borderRight: '2px solid #3182ce',  // Using Tailwind sss-blue equivalent
                        boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                        paddingTop: '25px', 
                    },
                }}
            >
                <Box
                    sx={{
                        padding: '0px 16px',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', marginBottom: '20px' }}>
                        Device Details
                    </Typography>
                </Box>
                <List component="ol">
                    {devices.map((device, index) => (
                        <ListItem 
                            button 
                            component="li" 
                            key={index} 
                            onClick={() => handleDeviceClick(device.deviceID)} 
                            sx={{ 
                                padding: '8px 16px', 
                                borderBottom: '1px solid #ddd', 
                                color: '#333'
                            }}
                        >
                            <ListItemText
                                primary={device.deviceID}
                                primaryTypographyProps={{
                                    style: {
                                        fontSize: '16px',
                                    }
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            </Drawer>

            <Box
                className="gradient-bg text-white p-4 rounded-md shadow-md mb-4 border border-gray-900 relative"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginLeft: sidebarOpen ? '250px' : '0',
                    transition: 'margin-left 0.3s ease, width 0.3s ease',
                    width: sidebarOpen ? 'calc(100% - 250px)' : '100%',
                }}
            >
                <button
                    onClick={() => navigate(-1)}
                    className="bg-transparent rounded-full shadow-none hover:bg-black/10 focus:outline-none mr-8 p-2"
                >
                    <ArrowBackIcon />
                </button>
                {/* Floating Action Button inside the header */}
                <Fab
                    color="primary"
                    aria-label="toggle-sidebar"
                    onClick={toggleDrawer(!sidebarOpen)}
                    sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        },
                        marginLeft: 2,
                    }}
                >
                    {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
                </Fab>
                <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>
                    {groupNumber}
                </Typography>
            </Box>

            <Box
                sx={{
                    marginLeft: sidebarOpen ? '250px' : '0',
                    transition: 'margin-left 0.3s ease, width 0.3s ease',
                    width: sidebarOpen ? 'calc(100% - 250px)' : '100%',
                }}
            >
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-300">
                    <Typography variant="h6" fontWeight="bold" className="mb-4" sx={{ textAlign: 'left', color: '#333' }}>
                        Device List
                    </Typography>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-300 rounded-md">
                            <thead className="bg-gray-100">
                                <tr>
                                    {['Tracking Number', 'Device ID', 'Device Type', 'Inspection Type', 'Periodic Inspection', 'Filing Due', 'Related Cycle End Date', 'Last Day To File'].map((header) => (
                                        <th key={header} className="py-2 px-4 border-b-2 border-gray-300 font-bold text-left" style={{ color: '#333' }}>{header}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {devices.map((device, index) => (
                                    <tr key={index} className="odd:bg-gray-50">
                                        <td className="py-2 px-4 border-b border-gray-300 text-xs" style={{ color: '#333' }}>{device.trackingNumber}</td>
                                        <td className="py-2 px-4 border-b border-gray-300 text-xs" style={{ color: '#333' }}>{device.deviceID}</td>
                                        <td className="py-2 px-4 border-b border-gray-300 text-xs" style={{ color: '#333' }}>{device.deviceType}</td>
                                        <td className="py-2 px-4 border-b border-gray-300 text-xs" style={{ color: '#333' }}>{device.inspectionType}</td>
                                        <td className="py-2 px-4 border-b border-gray-300 text-xs" style={{ color: '#333' }}>{device.periodicInspection || 'N/A'}</td>
                                        <td className="py-2 px-4 border-b border-gray-300 text-xs" style={{ color: '#333' }}>{device.filingDue}</td>
                                        <td className="py-2 px-4 border-b border-gray-300 text-xs" style={{ color: '#333' }}>{device.relatedCycleEndDate}</td>
                                        <td className="py-2 px-4 border-b border-gray-300 text-xs" style={{ color: '#333' }}>{device.lastdayToFile}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Box>
        </Box>
    );
};

export default GroupNumberDevices;
